import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBoxesStacked } from '@fortawesome/free-solid-svg-icons';

import StockRequests from './StockRequests';
import StockCatalog from './StockCatalog';
import RequestModal from './RequestModal';
import OrderModal from './OrderModal';
import ItemModal from './ItemModal';
import InvoiceModal from './InvoiceModal';
import DeliverModal from './DeliverModal';
import ExpenseModal from './ExpenseModal';
import { NoteModal, ConfirmModal } from './StockModal';
import './stock.css';

/**
 * Warehouse stock.
 *
 * 2026-08-26. API: routes/stock.js · schema: scripts/migrations/2026_08_warehouse_stock_1_core.sql
 *
 * TWO SURFACES, ONE JOB. The Requests board is the operational half — what
 * needs buying, what is on its way, what landed. The Catalogue is the
 * reference half — what we buy, where from, and how long it takes. Staff live
 * on the board; managers cross into the catalogue when something new starts
 * being bought.
 *
 * TWO AUDIENCES, ONE PAGE. The warehouse floor sees a deliberately smaller
 * screen: no buying links, no prices, no order references, no invoices, no
 * bulk selection — none of that is theirs to act on, and every extra field is
 * something to read past on a phone in a store room. They get the item, how
 * urgent it is, when it is expected, and one button. `isManager` gates the
 * rest, mirroring what the API itself will and will not allow.
 *
 * ONE FETCH, ONE STATE. Both tabs read the same items/requests arrays held
 * here, so an action taken on the board (marking something ordered) is visible
 * in the catalogue the moment it lands, with no second refresh and no chance
 * of the two disagreeing about whether an item is already requested.
 */

const OFFICE_ROLES = ['admin', 'manager', 'supervisor'];

export default function Stock() {
    const userRole = localStorage.getItem('userRole');
    // Mirrors authorizeAdminOrManager on the API — the roles that may edit the
    // catalogue, mark things ordered and file invoices. The UI hides what the
    // API refuses.
    const isManager = OFFICE_ROLES.includes(userRole);

    // The floor's day starts at the shelf, not the board: they open this page
    // to say something is running out. Managers open it to see what to buy.
    const [tab, setTab] = useState(isManager ? 'requests' : 'catalog');

    const [categories, setCategories] = useState([]);
    const [items, setItems] = useState([]);
    const [requests, setRequests] = useState([]);
    const [orders, setOrders] = useState([]);
    const [summary, setSummary] = useState(null);

    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState('');

    const [categoryId, setCategoryId] = useState(null);   // null = all
    const [showArchived, setShowArchived] = useState(false);

    /* Modals — null when closed, a payload object when open. */
    const [requestFor, setRequestFor] = useState(null);   // { item } | { request }
    const [orderFor, setOrderFor] = useState(null);       // { requests: [] }
    const [editingItem, setEditingItem] = useState(null); // {} = new, {id} = edit
    const [invoiceFor, setInvoiceFor] = useState(null);
    const [expenseFor, setExpenseFor] = useState(null);
    const [deliverFor, setDeliverFor] = useState(null);
    const [cancelFor, setCancelFor] = useState(null);
    const [cancelOrderFor, setCancelOrderFor] = useState(null);
    const [archiveFor, setArchiveFor] = useState(null);

    const load = useCallback(async ({ quiet = false } = {}) => {
        if (quiet) setRefreshing(true); else setLoading(true);
        try {
            const [cats, itemRes, reqRes, sumRes, orderRes] = await Promise.all([
                axios.get('/api/stock/categories'),
                axios.get('/api/stock/items', { params: { include_archived: true } }),
                axios.get('/api/stock/requests', { params: { limit: 300 } }),
                axios.get('/api/stock/requests/summary'),
                axios.get('/api/stock/orders', { params: { limit: 100 } }),
            ]);
            setCategories(cats.data?.categories || []);
            setItems(itemRes.data?.items || []);
            setRequests(reqRes.data?.requests || []);
            setSummary(sumRes.data?.summary || null);
            setOrders(orderRes.data?.orders || []);
            setError('');
        } catch (err) {
            setError(
                err.response?.status === 403
                    ? 'You do not have access to warehouse stock.'
                    : 'Could not load stock. Try again in a moment.'
            );
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const refresh = useCallback(() => load({ quiet: true }), [load]);

    /**
     * NO REFRESH BUTTON. Several people work this board at once — a manager
     * orders something while the warehouse has the page open — so the page
     * re-reads itself whenever it comes back to the front, and after every
     * action. Nobody has to know to press anything, which is the only version
     * of "keep it up to date" that actually works on a shop floor.
     */
    useEffect(() => {
        const onFocus = () => { if (!document.hidden) refresh(); };
        document.addEventListener('visibilitychange', onFocus);
        window.addEventListener('focus', onFocus);
        return () => {
            document.removeEventListener('visibilitychange', onFocus);
            window.removeEventListener('focus', onFocus);
        };
    }, [refresh]);

    /* ── Actions ─────────────────────────────────────────────────────────── */

    const submitRequest = async ({ itemId, urgency, requestId }) => {
        if (requestId) {
            await axios.put(`/api/stock/requests/${requestId}`, { urgency });
            toast.success(urgency === 'urgent' ? 'Marked urgent.' : 'Updated.');
        } else {
            await axios.post('/api/stock/requests', { item_id: itemId, urgency });
            toast.success('Requested — the managers have been notified.');
        }
        setRequestFor(null);
        await refresh();
    };

    const submitOrder = async ({ lines, reference }) => {
        // ONE ORDER, HOWEVER MANY LINES — see routes/stock.js POST /orders.
        const res = await axios.post('/api/stock/orders', { lines, reference: reference || null });
        const n = res.data?.order?.lines?.length || lines.length;
        toast.success(n === 1
            ? 'Marked ordered — the warehouse has been told.'
            : `Order placed for ${n} items — the warehouse has been told.`);
        setOrderFor(null);
        await refresh();
    };

    /* Receiving opens the delivery dialog — pictures, the note and the
       confirmation are one moment in one doorway. */
    const receiveOrder = (order) => setDeliverFor({ ...order, mode: 'receive' });

    const deliverDone = async ({ keepOpen = false } = {}) => {
        await refresh();
        if (keepOpen) {
            // Removing one picture leaves the dialog open on the rest, so the
            // list it shows has to be re-read from the fresh data.
            const fresh = await axios.get(`/api/stock/orders/${deliverFor.id}`);
            setDeliverFor(fresh.data?.order
                ? { ...fresh.data.order, mode: deliverFor.mode }
                : null);
        } else {
            toast.success(deliverFor.mode === 'receive'
                ? `Order #${deliverFor.id} received.`
                : 'Pictures saved.');
            setDeliverFor(null);
        }
    };

    const undoLine = async (line) => {
        try {
            await axios.post(`/api/stock/requests/${line.id}/undeliver`, {});
            toast.success(`${line.item_name} put back to on the way.`);
            await refresh();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Could not undo that.');
        }
    };

    const submitCancelOrder = async (reason) => {
        const res = await axios.post(`/api/stock/orders/${cancelOrderFor.id}/cancel`, { reason });
        const back = res.data?.reopened || [];
        const dropped = res.data?.dropped || [];
        toast.success(
            `Order #${cancelOrderFor.id} cancelled`
            + (back.length ? ` — ${back.length} item${back.length === 1 ? '' : 's'} back on the to-order list.` : '.')
        );
        // Said out loud rather than silently swallowed: a line that had already
        // been requested again did NOT come back, and the manager should know.
        if (dropped.length) {
            toast.warn(`${dropped.join(', ')} had already been requested again, so ${dropped.length === 1 ? 'it was' : 'they were'} not reopened.`);
        }
        setCancelOrderFor(null);
        await refresh();
    };

    const submitCancel = async (reason) => {
        await axios.post(`/api/stock/requests/${cancelFor.id}/cancel`, { reason });
        toast.success('Request cancelled.');
        setCancelFor(null);
        await refresh();
    };

    const submitItem = async (payload) => {
        if (payload.id) {
            await axios.put(`/api/stock/items/${payload.id}`, payload);
            toast.success(`${payload.name} saved.`);
        } else {
            await axios.post('/api/stock/items', payload);
            toast.success(`${payload.name} added to the catalogue.`);
        }
        setEditingItem(null);
        await refresh();
    };

    // The invoice dialog stays open after a save (owner, 2026-09-07 — several
    // documents per order): the refreshed order shows what was just filed and
    // more can follow. `added` is how many files that save put on the order.
    const invoiceSaved = async ({ keepOpen = false, added = 0 } = {}) => {
        await refresh();
        if (added > 0) toast.success(added === 1 ? 'Invoice filed.' : `${added} files filed.`);
        if (keepOpen) {
            const fresh = await axios.get(`/api/stock/orders/${invoiceFor.id}`);
            setInvoiceFor(fresh.data?.order || null);
        } else {
            setInvoiceFor(null);
        }
    };

    /* ONE ZOHO EXPENSE PER SUPPLIER INVOICE (owner, 2026-09-12). An order with
       a single invoice still to book (or none — the dialog then asks for one)
       goes straight to the expense dialog; with several, the invoice list
       opens and each invoice is recorded from its own row. */
    const openExpense = (order) => {
        const docs = order?.invoices || [];
        const open = docs.filter((d) => !d.in_zoho);
        if (docs.length > 0 && open.length !== 1) { setInvoiceFor(order); return; }
        setExpenseFor({ order, invoice: open[0] || null });
    };

    /* Archiving asks first — it sits next to Edit on every card and a mis-tap
       used to remove an item from everybody's list silently. Restoring does
       not ask: putting something back is not a thing to regret. */
    const archiveItem = async (item, archived) => {
        if (archived) { setArchiveFor(item); return; }
        try {
            await axios.put(`/api/stock/items/${item.id}/archived`, { archived: false });
            toast.success(`${item.name} is back in the catalogue.`);
            await refresh();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Could not restore that item.');
        }
    };

    const confirmArchive = async () => {
        await axios.put(`/api/stock/items/${archiveFor.id}/archived`, { archived: true });
        toast.success(`${archiveFor.name} archived — its history stays.`);
        setArchiveFor(null);
        await refresh();
    };

    /* ── Derived ─────────────────────────────────────────────────────────── */

    const visibleCategories = useMemo(
        () => categories.filter((c) => c.is_active),
        [categories]
    );

    const counts = summary || {
        to_order: 0, urgent: 0, on_the_way: 0, delivered_7d: 0,
        oldest_waiting_days: 0, missing_invoice: 0, not_in_zoho: 0,
    };

    if (loading) {
        return (
            <div className="stk">
                <div className="stk__loading">Loading stock…</div>
            </div>
        );
    }

    return (
        <div className={`stk ${isManager ? '' : 'stk--simple'}`}>
            <header className="stk__head">
                <div className="stk__headMain">
                    <h2 className="stk__title">
                        <FontAwesomeIcon icon={faBoxesStacked} /> Stock
                    </h2>
                    <p className="stk__sub">
                        {isManager
                            ? 'What the warehouse needs, what has been ordered, and what has landed.'
                            : 'Tell the office what is running out, and confirm what arrives.'}
                    </p>
                </div>
                {refreshing ? <span className="stk__updating">Updating…</span> : null}
            </header>

            {error ? <div className="stk__error">{error}</div> : null}

            <div className={`stk__stats ${isManager ? '' : 'stk__stats--simple'}`}>
                <Stat
                    label={isManager ? 'To order' : 'Asked for'}
                    value={counts.to_order}
                    tone={counts.to_order ? 'warn' : 'calm'}
                    foot={
                        counts.urgent
                            ? `${counts.urgent} urgent`
                            : counts.to_order
                                ? `longest wait ${counts.oldest_waiting_days}d`
                                : 'nothing waiting'
                    }
                    footTone={counts.urgent ? 'danger' : null}
                    onClick={() => setTab('requests')}
                />
                <Stat
                    label="On the way"
                    value={counts.on_the_way}
                    tone="info"
                    foot="ordered, not yet received"
                    onClick={() => setTab('requests')}
                />
                {isManager && (
                    <Stat
                        label="Missing invoice"
                        value={counts.missing_invoice}
                        tone={counts.missing_invoice ? 'danger' : 'calm'}
                        foot={counts.missing_invoice ? 'orders with no document filed' : 'every order is documented'}
                        footTone={counts.missing_invoice ? 'danger' : null}
                        onClick={() => setTab('requests')}
                    />
                )}
                {isManager && (
                    <Stat
                        label="Not in Zoho"
                        value={counts.not_in_zoho}
                        tone={counts.not_in_zoho ? 'danger' : 'calm'}
                        foot={counts.not_in_zoho ? 'purchases not yet in the books' : 'every purchase is booked'}
                        footTone={counts.not_in_zoho ? 'danger' : null}
                        onClick={() => setTab('requests')}
                    />
                )}
            </div>

            <div className="stk__tabs" role="tablist">
                <button
                    type="button"
                    role="tab"
                    aria-selected={tab === 'requests'}
                    className={`stk__tab ${tab === 'requests' ? 'is-active' : ''}`}
                    onClick={() => setTab('requests')}
                >
                    Requests
                    {counts.to_order > 0 && <span className="stk__tabCount">{counts.to_order}</span>}
                </button>
                <button
                    type="button"
                    role="tab"
                    aria-selected={tab === 'catalog'}
                    className={`stk__tab ${tab === 'catalog' ? 'is-active' : ''}`}
                    onClick={() => setTab('catalog')}
                >
                    {isManager ? 'Catalogue' : 'Stock list'}
                    <span className="stk__tabCount stk__tabCount--muted">
                        {items.filter((i) => i.is_active).length}
                    </span>
                </button>
            </div>

            {tab === 'requests' ? (
                <StockRequests
                    requests={requests}
                    orders={orders}
                    isManager={isManager}
                    onOrder={(list) => setOrderFor({ requests: list })}
                    onUndoLine={undoLine}
                    onReceiveOrder={receiveOrder}
                    onExpense={openExpense}
                    onCancelOrder={setCancelOrderFor}
                    onCancel={setCancelFor}
                    onEdit={(request) => setRequestFor({ request })}
                    onInvoice={setInvoiceFor}
                    onGoCatalog={() => setTab('catalog')}
                />
            ) : (
                <StockCatalog
                    items={items}
                    categories={visibleCategories}
                    categoryId={categoryId}
                    onCategory={setCategoryId}
                    showArchived={showArchived}
                    onShowArchived={setShowArchived}
                    isManager={isManager}
                    onRequest={(item) => setRequestFor({ item })}
                    onEditItem={setEditingItem}
                    onArchiveItem={archiveItem}
                    onAddItem={() => setEditingItem({})}
                />
            )}

            {requestFor && (
                <RequestModal
                    item={requestFor.item}
                    request={requestFor.request}
                    onClose={() => setRequestFor(null)}
                    onSubmit={submitRequest}
                />
            )}

            {orderFor && (
                <OrderModal
                    requests={orderFor.requests}
                    onClose={() => setOrderFor(null)}
                    onSubmit={submitOrder}
                />
            )}

            {editingItem && (
                <ItemModal
                    item={editingItem.id ? items.find((i) => i.id === editingItem.id) : null}
                    categories={visibleCategories}
                    defaultCategoryId={categoryId}
                    onClose={() => setEditingItem(null)}
                    onSubmit={submitItem}
                />
            )}

            {expenseFor && (
                <ExpenseModal
                    order={expenseFor.order}
                    invoice={expenseFor.invoice}
                    onClose={() => setExpenseFor(null)}
                    onNeedInvoice={() => { setInvoiceFor(expenseFor.order); setExpenseFor(null); }}
                    onDone={async (result) => {
                        // The attachment is best-effort on the server — if the PDF
                        // did not go up, the expense still did, and saying so is
                        // more useful than a green tick that hides it.
                        if (result?.attachment?.error) {
                            toast.warn(result.attachment.error);
                        } else {
                            const docs = Number(result?.attachment?.documents || 0);
                            const left = Number(result?.remaining || 0);
                            toast.success(
                                `Recorded in Zoho — AED ${Number(result.total).toFixed(2)}`
                                + (result.vat_applied ? ' incl. VAT' : ' (no VAT)')
                                + (docs > 1 ? ` · ${docs} files attached as one PDF` : '')
                                + (left ? ` · ${left} more invoice${left === 1 ? '' : 's'} on this order to record` : '')
                            );
                        }
                        setExpenseFor(null);
                        await refresh();
                        // Another supplier's invoice still to book: straight back
                        // to the list so it can be done in the same sitting.
                        if (Number(result?.remaining || 0) > 0 && result?.order) setInvoiceFor(result.order);
                    }}
                />
            )}

            {invoiceFor && (
                <InvoiceModal
                    order={invoiceFor}
                    onClose={() => setInvoiceFor(null)}
                    onSaved={invoiceSaved}
                    onRecord={(invoice) => { setExpenseFor({ order: invoiceFor, invoice }); setInvoiceFor(null); }}
                />
            )}

            {deliverFor && (
                <DeliverModal
                    order={deliverFor}
                    mode={deliverFor.mode || 'receive'}
                    canRemove={isManager}
                    onClose={() => setDeliverFor(null)}
                    onDone={deliverDone}
                />
            )}

            {archiveFor && (
                <ConfirmModal
                    title={`Archive ${archiveFor.name}?`}
                    message={
                        <>
                            It comes off the stock list for everyone, so nobody can request it.
                            Every past request and order that named it stays exactly as it is,
                            and you can put it back at any time with <strong>Show archived</strong>.
                        </>
                    }
                    confirmLabel="Archive it"
                    tone="danger"
                    onClose={() => setArchiveFor(null)}
                    onConfirm={confirmArchive}
                />
            )}

            {cancelOrderFor && (
                <NoteModal
                    title={`Cancel order #${cancelOrderFor.id}`}
                    intro={
                        <>
                            The {(cancelOrderFor.lines || []).filter((l) => l.status === 'ordered').length} item
                            {(cancelOrderFor.lines || []).filter((l) => l.status === 'ordered').length === 1 ? '' : 's'}
                            {' '}still on the way go back onto the to-order list, and the warehouse is told.
                            Anything already received stays received.
                        </>
                    }
                    label="Why is this order being cancelled?"
                    placeholder="e.g. supplier cannot supply until next month"
                    confirmLabel="Cancel order"
                    tone="danger"
                    required
                    onClose={() => setCancelOrderFor(null)}
                    onSubmit={submitCancelOrder}
                />
            )}

            {cancelFor && (
                <NoteModal
                    title={`Cancel request — ${cancelFor.item_name}`}
                    intro={<>{cancelFor.requested_by_name || 'The requester'} will be told this was cancelled.</>}
                    label="Why is this being cancelled?"
                    placeholder="e.g. we found two boxes in the back store"
                    confirmLabel="Cancel request"
                    tone="danger"
                    required
                    onClose={() => setCancelFor(null)}
                    onSubmit={submitCancel}
                />
            )}
        </div>
    );
}

function Stat({ label, value, foot, tone = 'calm', footTone, onClick }) {
    const Tag = onClick ? 'button' : 'div';
    return (
        <Tag
            type={onClick ? 'button' : undefined}
            onClick={onClick}
            className={`stk__stat stk__stat--${tone} ${onClick ? 'is-clickable' : ''}`}
        >
            <span className="stk__statLabel">{label}</span>
            <span className="stk__statValue">{value}</span>
            <span className={`stk__statFoot ${footTone ? `stk__statFoot--${footTone}` : ''}`}>{foot}</span>
        </Tag>
    );
}
